import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Search, ArrowDown, Check } from 'lucide-react';
import { getAllLoans } from '../store/slices/databaseSlice';

const SelectorContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const SelectorInputBase = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  cursor: text;
  
  &:hover {
    border-color: #c1c8cd;
  }
  
  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  ${props => props.isOpen && `
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}
`;

const SelectorInput = ({ isOpen, ...props }) => (
  <SelectorInputBase isOpen={isOpen} {...props} />
);

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 1rem;
  color: #374151;
  background: transparent;
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(Search)`
  width: 20px;
  height: 20px;
  color: #9ca3af;
  margin-right: 0.75rem;
`;

const DropdownIconBase = styled(ArrowDown)`
  width: 16px;
  height: 16px;
  color: #6b7280;
  transition: transform 0.2s ease;
  
  ${props => props.isOpen && `
    transform: rotate(180deg);
  `}
`;

const DropdownIcon = ({ isOpen, ...props }) => (
  <DropdownIconBase isOpen={isOpen} {...props} />
);

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #3b82f6;
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9fafb;
  }
  
  ${props => props.isSelected && `
    background-color: #eff6ff;
    color: #1d4ed8;
  `}
`;

const LoanId = styled.span`
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  font-weight: 500;
  color: #374151;
  
  ${props => props.isSelected && `
    color: #1d4ed8;
  `}
`;

const CheckIcon = styled(Check)`
  width: 16px;
  height: 16px;
  color: #10b981;
  margin-left: auto;
  opacity: ${props => props.isSelected ? 1 : 0};
`;

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const LoadingState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const LoanSelector = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLoanId, setSelectedLoanId] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const { allLoans, loading } = useSelector(state => state.database);
  
  // Load all loans on component mount
  useEffect(() => {
    dispatch(getAllLoans());
  }, [dispatch]);
  
  // Filter loans based on search term
  const filteredLoans = allLoans.filter(loanId => 
    loanId.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedLoanId(value);
    setIsOpen(true);
  };
  
  // Handle loan selection
  const handleLoanSelect = (loanId) => {
    setSelectedLoanId(loanId);
    setSearchTerm(loanId);
    setIsOpen(false);
    navigate(`/document-analysis/${loanId}`);
  };
  
  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <SelectorContainer ref={containerRef}>
      <SelectorInput isOpen={isOpen} onClick={() => inputRef.current?.focus()}>
        <SearchIcon />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search for a loan ID..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        <DropdownIcon isOpen={isOpen} />
      </SelectorInput>
      
      {isOpen && (
        <Dropdown>
          {loading.getAllLoans ? (
            <LoadingState>Loading loans...</LoadingState>
          ) : filteredLoans.length > 0 ? (
            filteredLoans.map((loanId) => (
              <DropdownItem
                key={loanId}
                onClick={() => handleLoanSelect(loanId)}
                isSelected={loanId === selectedLoanId}
              >
                <LoanId isSelected={loanId === selectedLoanId}>
                  {loanId}
                </LoanId>
                <CheckIcon isSelected={loanId === selectedLoanId} />
              </DropdownItem>
            ))
          ) : (
            <EmptyState>
              {searchTerm ? 'No loans found matching your search' : 'No loans available'}
            </EmptyState>
          )}
        </Dropdown>
      )}
    </SelectorContainer>
  );
};

export default LoanSelector;
